import React, { useState } from 'react';
import { PlusIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, UsersIcon, UserPlusIcon, AlertTriangleIcon, ClockIcon, CheckCircleIcon, CalendarIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, BellIcon, CheckIcon, XIcon } from 'lucide-react';
import { RequisitionList } from './procurement/RequisitionList';
import { PurchaseOrderList } from './procurement/PurchaseOrderList';
import { formatCurrency } from './utils/currencyFormatter';
import { Remi } from './ai/Remi';
import { getDefaultCurrency } from '../utils/appSettings';
export function Dashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  const defaultCurrency = getDefaultCurrency();
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for new dashboard sections
  const vendorStats = {
    pendingOnboarding: 5,
    failingCompliance: 3,
    awaitingReview: 7
  };
  const invoiceStats = {
    inProcessing: 12,
    failingCompliance: 4,
    approvedForPayment: 8
  };
  const vendorsNeedingAction = [{
    id: 'VEN-2023-004',
    name: 'Global Supply Co.',
    status: 'Incomplete',
    issue: 'Missing tax information',
    dueDate: '2023-11-15'
  }, {
    id: 'VEN-2023-005',
    name: 'Tech Solutions Inc.',
    status: 'Flagged',
    issue: 'Insurance certificate expired',
    dueDate: '2023-11-10'
  }, {
    id: 'VEN-2023-006',
    name: 'Office Essentials',
    status: 'Awaiting Review',
    issue: 'New onboarding submission',
    dueDate: '2023-11-20'
  }];
  const invoicesNeedingAttention = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-001234',
    vendor: 'Dell Technologies',
    issue: 'Unmatched PO',
    status: 'Error',
    amount: 4500.0,
    currency: defaultCurrency,
    dueDate: '2023-11-12'
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-005678',
    vendor: 'Office Depot',
    issue: 'Missing GRN',
    status: 'Warning',
    amount: 1250.75,
    currency: defaultCurrency,
    dueDate: '2023-11-15'
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-009012',
    vendor: 'PrintWorks Inc.',
    issue: 'Currency mismatch',
    status: 'Error',
    amount: 3200.5,
    currency: 'EUR',
    dueDate: '2023-11-18'
  }];
  const upcomingRenewals = [{
    id: 'DOC-2023-001',
    vendor: 'Dell Technologies',
    documentType: 'Insurance Certificate',
    expiryDate: '2023-11-30',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-002',
    vendor: 'Global Supply Co.',
    documentType: 'Tax Compliance',
    expiryDate: '2023-12-05',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-003',
    vendor: 'Tech Solutions Inc.',
    documentType: 'Business License',
    expiryDate: '2023-12-15',
    status: 'Expiring Soon'
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-3858">
      <div className="mb-8 flex justify-between items-center" data-id="element-3859">
        <div data-id="element-3860">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3861">
            P2P Control Centre
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3862">
            Comprehensive overview of procurement, vendor management, and
            accounts payable
          </p>
        </div>
        <div className="flex items-center" data-id="element-3863">
          <div className="flex items-center" data-id="element-3864">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3865" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3866">
              <option value="7days" data-id="element-3867">Last 7 days</option>
              <option value="30days" data-id="element-3868">Last 30 days</option>
              <option value="90days" data-id="element-3869">Last 90 days</option>
              <option value="ytd" data-id="element-3870">Year to date</option>
            </select>
          </div>
        </div>
      </div>
      {/* Remi AI Assistant */}
      <div className="mb-8" data-id="element-3871">
        <Remi setCurrentView={setCurrentView} data-id="element-3872" />
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-3873">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3874">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3875">
          {/* Existing cards */}
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3876">
            <div className="p-5" data-id="element-3877">
              <div className="flex items-center" data-id="element-3878">
                <div className="flex-shrink-0" data-id="element-3879">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-3880" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3881">
                  <dl data-id="element-3882">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3883">
                      Total Requisitions
                    </dt>
                    <dd data-id="element-3884">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3885">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3886">
            <div className="p-5" data-id="element-3887">
              <div className="flex items-center" data-id="element-3888">
                <div className="flex-shrink-0" data-id="element-3889">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-3890" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3891">
                  <dl data-id="element-3892">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3893">
                      Pending Approval
                    </dt>
                    <dd data-id="element-3894">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3895">
                        {pendingApproval}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-3896">
            <div className="p-5" data-id="element-3897">
              <div className="flex items-center" data-id="element-3898">
                <div className="flex-shrink-0" data-id="element-3899">
                  <ClipboardCheckIcon className="h-6 w-6 text-indigo-400" data-id="element-3900" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3901">
                  <dl data-id="element-3902">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3903">
                      Purchase Orders
                    </dt>
                    <dd data-id="element-3904">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3905">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-3906">
            <div className="p-5" data-id="element-3907">
              <div className="flex items-center" data-id="element-3908">
                <div className="flex-shrink-0" data-id="element-3909">
                  <TrendingUpIcon className="h-6 w-6 text-green-400" data-id="element-3910" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3911">
                  <dl data-id="element-3912">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3913">
                      Total Spend
                    </dt>
                    <dd data-id="element-3914">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3915">
                        {formatCurrency(totalReqSpend + totalPOSpend, defaultCurrency, locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Vendor Status Summary Cards */}
      <div className="mb-8" data-id="element-3916">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3917">
          Vendor Management
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3918">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3919">
            <div className="p-5" data-id="element-3920">
              <div className="flex items-center" data-id="element-3921">
                <div className="flex-shrink-0" data-id="element-3922">
                  <UserPlusIcon className="h-6 w-6 text-yellow-400" data-id="element-3923" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3924">
                  <dl data-id="element-3925">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3926">
                      Vendors Pending Onboarding
                    </dt>
                    <dd data-id="element-3927">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3928">
                        {vendorStats.pendingOnboarding}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('compliance-check')} data-id="element-3929">
            <div className="p-5" data-id="element-3930">
              <div className="flex items-center" data-id="element-3931">
                <div className="flex-shrink-0" data-id="element-3932">
                  <AlertTriangleIcon className="h-6 w-6 text-orange-400" data-id="element-3933" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3934">
                  <dl data-id="element-3935">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3936">
                      <span className="group relative" data-id="element-3937">
                        Vendors Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3938">
                          Includes expired insurance, missing tax details, etc.
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3939">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3940">
                        {vendorStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3941">
            <div className="p-5" data-id="element-3942">
              <div className="flex items-center" data-id="element-3943">
                <div className="flex-shrink-0" data-id="element-3944">
                  <ClockIcon className="h-6 w-6 text-blue-400" data-id="element-3945" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3946">
                  <dl data-id="element-3947">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3948">
                      Vendors Awaiting Review
                    </dt>
                    <dd data-id="element-3949">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3950">
                        {vendorStats.awaitingReview}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Summary Cards */}
      <div className="mb-8" data-id="element-3951">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3952">
          Accounts Payable
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3953">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3954">
            <div className="p-5" data-id="element-3955">
              <div className="flex items-center" data-id="element-3956">
                <div className="flex-shrink-0" data-id="element-3957">
                  <ReceiptIcon className="h-6 w-6 text-purple-400" data-id="element-3958" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3959">
                  <dl data-id="element-3960">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3961">
                      Invoices in Processing
                    </dt>
                    <dd data-id="element-3962">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3963">
                        {invoiceStats.inProcessing}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('matching')} data-id="element-3964">
            <div className="p-5" data-id="element-3965">
              <div className="flex items-center" data-id="element-3966">
                <div className="flex-shrink-0" data-id="element-3967">
                  <AlertCircleIcon className="h-6 w-6 text-red-400" data-id="element-3968" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3969">
                  <dl data-id="element-3970">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3971">
                      <span className="group relative" data-id="element-3972">
                        Invoices Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3973">
                          Unmatched PO, blocked vendor, tax or value mismatches
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3974">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3975">
                        {invoiceStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3976">
            <div className="p-5" data-id="element-3977">
              <div className="flex items-center" data-id="element-3978">
                <div className="flex-shrink-0" data-id="element-3979">
                  <CreditCardIcon className="h-6 w-6 text-green-400" data-id="element-3980" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3981">
                  <dl data-id="element-3982">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3983">
                      Invoices Approved for Payment
                    </dt>
                    <dd data-id="element-3984">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3985">
                        {invoiceStats.approvedForPayment}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3986">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3987">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3988">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3989">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3990">
              View all
            </button>
          </div>
          <RequisitionList requisitions={recentRequisitions} locale={locale} data-id="element-3991" />
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3992">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3993">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3994">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3995">
              View all
            </button>
          </div>
          <PurchaseOrderList purchaseOrders={recentPurchaseOrders} locale={locale} data-id="element-3996" />
        </div>
      </div>
      {/* New Dashboard Sections */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3997">
        {/* Vendors Needing Action */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3998">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3999">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4000">
              Vendors Needing Action
            </h3>
            <button onClick={() => setCurrentView('vendor-management')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-4001">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-4002">
            {vendorsNeedingAction.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-4003">
                No vendors need attention at this time.
              </li> : vendorsNeedingAction.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('vendor-management')} data-id="element-4004">
                  <div className="flex items-center justify-between" data-id="element-4005">
                    <div className="flex items-center" data-id="element-4006">
                      <div className="flex-shrink-0" data-id="element-4007">
                        {vendor.status === 'Incomplete' ? <XIcon className="h-5 w-5 text-red-400" data-id="element-4008" /> : vendor.status === 'Flagged' ? <AlertTriangleIcon className="h-5 w-5 text-orange-400" data-id="element-4009" /> : <ClockIcon className="h-5 w-5 text-blue-400" data-id="element-4010" />}
                      </div>
                      <div className="ml-3" data-id="element-4011">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-4012">
                          {vendor.name}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-4013">{vendor.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-4014">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-4015">
                        Due: {vendor.dueDate}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('vendor-management');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-4016">
                        Review
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Invoices Needing Attention */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-4017">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4018">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4019">
              Invoices Needing Attention
            </h3>
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-4020">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-4021">
            {invoicesNeedingAttention.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-4022">
                No invoices need attention at this time.
              </li> : invoicesNeedingAttention.map(invoice => <li key={invoice.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('invoice-list')} data-id="element-4023">
                  <div className="flex items-center justify-between" data-id="element-4024">
                    <div className="flex items-center" data-id="element-4025">
                      <div className="flex-shrink-0" data-id="element-4026">
                        {invoice.status === 'Error' ? <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-4027" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-4028" />}
                      </div>
                      <div className="ml-3" data-id="element-4029">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-4030">
                          {invoice.invoiceNumber} - {invoice.vendor}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-4031">{invoice.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-4032">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-4033">
                        {formatCurrency(invoice.amount, invoice.currency, locale)}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('matching');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-4034">
                        Resolve
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
      {/* Upcoming Renewals / Expiries */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-4035">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4036">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4037">
            Upcoming Renewals & Expiries
          </h3>
          <button onClick={() => setCurrentView('compliance-check')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-4038">
            View all
          </button>
        </div>
        <ul className="divide-y divide-gray-200" data-id="element-4039">
          {upcomingRenewals.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-4040">
              No upcoming renewals or expiries.
            </li> : upcomingRenewals.map(renewal => <li key={renewal.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('compliance-check')} data-id="element-4041">
                <div className="flex items-center justify-between" data-id="element-4042">
                  <div className="flex items-center" data-id="element-4043">
                    <div className="flex-shrink-0" data-id="element-4044">
                      <CalendarIcon className="h-5 w-5 text-orange-400" data-id="element-4045" />
                    </div>
                    <div className="ml-3" data-id="element-4046">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-4047">
                        {renewal.vendor} - {renewal.documentType}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-4048">
                        Expires on {renewal.expiryDate}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-4049">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-4050">
                      <ClockIcon className="mr-1 h-3 w-3" data-id="element-4051" />
                      {renewal.status}
                    </span>
                  </div>
                </div>
              </li>)}
        </ul>
      </div>
      {/* Optional: Vendor Compliance Trend Chart */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8 p-5" data-id="element-4052">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-4053">
          Vendor Compliance Trend (Last 3 Months)
        </h3>
        <div className="h-64 flex items-center justify-center" data-id="element-4054">
          <div className="w-full h-full flex items-end justify-around px-10" data-id="element-4055">
            <div className="flex flex-col items-center" data-id="element-4056">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '70%'
            }} data-id="element-4057"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-4058">Sep</p>
              <p className="text-xs text-gray-400" data-id="element-4059">85%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-4060">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '82%'
            }} data-id="element-4061"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-4062">Oct</p>
              <p className="text-xs text-gray-400" data-id="element-4063">92%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-4064">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '78%'
            }} data-id="element-4065"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-4066">Nov</p>
              <p className="text-xs text-gray-400" data-id="element-4067">89%</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}